// Upload Papers dialog (Step 4). Multi-file picker → classification (PDF
// batch / image merge / mixed-rejected) → per-paper rows with name fields →
// submit. On 409 duplicate, the row offers inline [Keep existing] / [Replace]
// resolution. Each paper is uploaded independently so partial failure is
// fine (Step 4 "Per-file success/failure").

import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";
import { useSettings } from "../api/hooks";
import type { Paper } from "../api/types";

import "./CreateProjectModal.css";
import "./UploadPapersDialog.css";

interface Props {
  projectId: number;
  onClose: () => void;
  onComplete: () => void;
}

type Kind = "pdf" | "image" | "unknown";

const PDF_EXT = [".pdf"];
const IMG_EXT = [".jpg", ".jpeg", ".png", ".tif", ".tiff"];
const PDF_TYPES = new Set(["application/pdf"]);
const IMG_TYPES = new Set(["image/jpeg", "image/png", "image/tiff"]);
const ACCEPT = ".pdf,image/jpeg,image/png,image/tiff,.jpg,.png,.tif,.tiff";

type RowStatus =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "duplicate"; existing: { id: number; name: string; status: string } }
  | { kind: "done"; paper: Paper }
  | { kind: "error"; message: string };

// A "paper" being prepared for upload.
//   * PDF mode → one row per selected PDF (each row has one file)
//   * Image mode → exactly one row (multiple files merged into one paper)
interface UploadRow {
  id: string;
  files: File[];
  name: string;
  status: RowStatus;
}

export function UploadPapersDialog({ projectId, onClose, onComplete }: Props) {
  const { t } = useTranslation();
  const headingId = useId();
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [mode, setMode] = useState<"empty" | "pdf" | "image" | "mixed">("empty");
  const [pickerError, setPickerError] = useState<string | null>(null);
  const { data: settings } = useSettings();

  const submitting = useMemo(
    () => rows.some((r) => r.status.kind === "uploading"),
    [rows],
  );
  const allDone =
    rows.length > 0 &&
    rows.every(
      (r) => r.status.kind === "done" || r.status.kind === "error",
    );

  function onFilesPicked(picked: FileList | null) {
    setPickerError(null);
    if (!picked || picked.length === 0) return;

    const files = Array.from(picked);
    const kinds = files.map(kindOf);
    if (kinds.includes("unknown")) {
      setPickerError(t("upload_dialog.error_unsupported"));
      return;
    }
    const hasPdf = kinds.includes("pdf");
    const hasImg = kinds.includes("image");
    if (hasPdf && hasImg) {
      setPickerError(
        t("upload_dialog.error_mixed"),
      );
      return;
    }

    if (hasPdf) {
      setMode("pdf");
      setRows(
        files.map((f, i) => ({
          id: `${Date.now()}-${i}`,
          files: [f],
          name: filenameStem(f.name),
          status: { kind: "idle" },
        })),
      );
    } else {
      setMode("image");
      setRows([
        {
          id: `${Date.now()}-img`,
          files,
          name: "",
          status: { kind: "idle" },
        },
      ]);
    }
  }

  async function uploadRow(row: UploadRow, onDuplicate: "fail" | "replace" = "fail") {
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id ? { ...r, status: { kind: "uploading" } } : r,
      ),
    );

    const fd = new FormData();
    fd.append("project_id", String(projectId));
    fd.append("name", row.name.trim());
    fd.append("on_duplicate", onDuplicate);
    for (const f of row.files) fd.append("files", f);

    let res: Response;
    try {
      res = await apiFetch("/api/papers/upload", {
        method: "POST",
        body: fd,
      });
    } catch (err) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? { ...r, status: { kind: "error", message: (err as Error).message } }
            : r,
        ),
      );
      return;
    }

    if (res.status === 409) {
      const body = (await safeJson(res)) as
        | { detail?: { existing?: { id: number; name: string; status: string } } }
        | null;
      const existing = body?.detail?.existing;
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id && existing
            ? { ...r, status: { kind: "duplicate", existing } }
            : r,
        ),
      );
      return;
    }

    if (!res.ok) {
      const body = (await safeJson(res)) as { detail?: unknown } | null;
      const raw = body?.detail;
      const message =
        typeof raw === "string"
          ? raw
          : Array.isArray(raw)
            ? (raw as { msg?: string }[]).map((e) => e.msg ?? JSON.stringify(e)).join("; ")
            : `Upload failed (${res.status})`;
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, status: { kind: "error", message } } : r,
        ),
      );
      return;
    }

    const paper = (await safeJson(res)) as Paper;
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, status: { kind: "done", paper } } : r)),
    );
    onComplete();
  }

  async function submitAll() {
    const toSubmit = rows.filter((r) => r.status.kind === "idle" && r.name.trim().length > 0);
    // Fire sequentially to keep server load predictable; per-row UI already
    // shows the in-progress one.
    for (const row of toSubmit) {
      // eslint-disable-next-line no-await-in-loop
      await uploadRow(row);
    }
  }

  const canSubmit = !submitting && rows.some((r) => r.status.kind === "idle" && r.name.trim().length > 0);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) {
          if (allDone) onClose();
          else if (!rows.some((r) => r.status.kind === "uploading")) onClose();
        }
      }}
    >
      <div
        className="modal-card upload-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
      >
        <button type="button" className="modal-close" onClick={onClose} disabled={submitting} aria-label="Close">✕</button>

        <h2 id={headingId} className="modal-card__title">Add Papers</h2>

        <label className="upload-picker">
          <input
            type="file"
            multiple
            accept={ACCEPT}
            onChange={(e) => onFilesPicked(e.target.files)}
            disabled={submitting}
          />
          Drop PDFs or images here, or click to choose…
          <span className="upload-picker__hint">One PDF per paper, or several images per paper.</span>
          {settings?.workspace_path && (
            <span className="upload-picker__hint" style={{ opacity: 0.7 }}>
              Workspace: {settings.workspace_path}
            </span>
          )}
        </label>

        {pickerError && (
          <div className="modal-error" role="alert">
            {pickerError}
          </div>
        )}

        {mode === "image" && rows.length === 1 && (
          <ImageOrderEditor
            row={rows[0]}
            disabled={submitting || rows[0].status.kind !== "idle"}
            onReorder={(next) =>
              setRows((prev) => prev.map((r) => (r.id === rows[0].id ? { ...r, files: next } : r)))
            }
          />
        )}

        {rows.length > 0 && (
          <ul className="upload-rows">
            {rows.map((row) => (
              <li key={row.id} className="upload-row">
                <span className="upload-row__count" aria-hidden>
                  {row.files.length === 1
                    ? row.files[0].name
                    : `${row.files.length} images`}
                </span>
                <input
                  type="text"
                  className="upload-row__name"
                  value={row.name}
                  placeholder={t("upload_dialog.student_name_placeholder")}
                  disabled={row.status.kind === "uploading" || row.status.kind === "done"}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, name: e.target.value } : r,
                      ),
                    )
                  }
                />
                <RowStatusCell
                  row={row}
                  onReplace={() => uploadRow(row, "replace")}
                  onSkip={() =>
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === row.id
                          ? {
                              ...r,
                              status: {
                                kind: "error",
                                message: t("upload_dialog.kept_existing"),
                              },
                            }
                          : r,
                      ),
                    )
                  }
                  onRetry={() => uploadRow(row, "fail")}
                />
              </li>
            ))}
          </ul>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-btn modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {allDone ? t("common.close") : t("common.cancel")}
          </button>
          <button
            type="button"
            className="modal-btn modal-btn--accent"
            disabled={!canSubmit}
            onClick={submitAll}
          >
            {submitting ? t("upload_dialog.uploading") : t("upload_dialog.upload_btn")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────

function kindOf(file: File): Kind {
  const ct = (file.type || "").toLowerCase();
  const dot = file.name.lastIndexOf(".");
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
  if (PDF_TYPES.has(ct) || PDF_EXT.includes(ext)) return "pdf";
  if (IMG_TYPES.has(ct) || IMG_EXT.includes(ext)) return "image";
  return "unknown";
}

function filenameStem(name: string): string {
  const dot = name.lastIndexOf(".");
  return (dot > 0 ? name.slice(0, dot) : name).replace(/[_-]+/g, " ").trim();
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// ── row status cell ───────────────────────────────────────────

interface RowStatusProps {
  row: UploadRow;
  onReplace: () => void;
  onSkip: () => void;
  onRetry: () => void;
}

function RowStatusCell({ row, onReplace, onSkip, onRetry }: RowStatusProps) {
  switch (row.status.kind) {
    case "idle":
      return <span className="upload-row__status">Ready</span>;
    case "uploading":
      return <span className="upload-row__status upload-row__status--busy">Uploading…</span>;
    case "done":
      return (
        <span className="upload-row__status upload-row__status--ok" title="Queued">
          ✓ Queued
        </span>
      );
    case "duplicate":
      return (
        <div className="upload-row__dup">
          <span className="upload-row__status upload-row__status--warn">
            Duplicate: {row.status.existing.name}
          </span>
          <div className="upload-row__dup-actions">
            <button type="button" className="upload-row__chip" onClick={onSkip}>
              Keep existing
            </button>
            <button
              type="button"
              className="upload-row__chip upload-row__chip--accent"
              onClick={onReplace}
            >
              Replace
            </button>
          </div>
        </div>
      );
    case "error":
      return (
        <div className="upload-row__err">
          <span className="upload-row__status upload-row__status--err">
            {row.status.message}
          </span>
          <button type="button" className="upload-row__chip" onClick={onRetry}>
            Retry
          </button>
        </div>
      );
  }
}

// ── image-merge order editor ──────────────────────────────────

interface OrderProps {
  row: UploadRow;
  disabled: boolean;
  onReorder: (next: File[]) => void;
}

function ImageOrderEditor({ row, disabled, onReorder }: OrderProps) {
  function swap(i: number, j: number) {
    if (j < 0 || j >= row.files.length) return;
    const next = row.files.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onReorder(next);
  }

  return (
    <details className="upload-order" open>
      <summary>Page order ({row.files.length} images)</summary>
      <ol className="upload-order__list">
        {row.files.map((f, i) => (
          <li key={`${f.name}-${i}`} className="upload-order__item">
            <span className="upload-order__name">{f.name}</span>
            <span className="upload-order__buttons">
              <button
                type="button"
                disabled={disabled || i === 0}
                onClick={() => swap(i, i - 1)}
                aria-label="Move up"
                title="Move up"
              >
                ▲
              </button>
              <button
                type="button"
                disabled={disabled || i === row.files.length - 1}
                onClick={() => swap(i, i + 1)}
                aria-label="Move down"
                title="Move down"
              >
                ▼
              </button>
            </span>
          </li>
        ))}
      </ol>
      <p className="upload-order__hint">
        Server also sorts by trailing number in filename. Use the buttons only
        when the filenames don't have a clear page suffix.
      </p>
    </details>
  );
}
